import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useAviso } from '../../contexts/AvisoContext';
import { useKanban } from '../../contexts/KanbanContext';
import { useSidebar } from '../../contexts/SidebarContext';
import { useDepartamento } from '../../contexts/DepartamentoContext';
import tarefaService from '../../services/tarefaService';

const Sidebar: React.FC = () => {
  const { usuario, logout } = useAuth();
  const { menuAberto, fecharMenu } = useSidebar();
  const { avisosNaoLidos } = useAviso();
  const { recarregarTarefas } = useKanban();
  const { ehLiderDepartamento } = useDepartamento();
  const location = useLocation();
  
  // Verificação direta se o usuário é líder (tanto eh_lider quanto ehLider)
  // Usamos apenas o contexto para evitar duplicação
  // const usuarioEhLider = usuario?.eh_lider === true || usuario?.ehLider === true;
  
  // Estado próprio para contador de tarefas pendentes
  const [tarefasPendentes, setTarefasPendentes] = useState<number>(0);
  
  const buscarTarefasPendentes = async () => {
    if (!usuario) return;
    try {
      const contador = await tarefaService.contarTarefasPendentes();
      setTarefasPendentes(contador);
    } catch (error) {
      // silently ignore
    }
  };

  useEffect(() => {
    if (!usuario) return;
    buscarTarefasPendentes();
    const interval = setInterval(buscarTarefasPendentes, 300000); // 5 minutos
    const handleFocus = () => buscarTarefasPendentes();
    window.addEventListener('focus', handleFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
    };
  }, [usuario]);

  useEffect(() => {
    if (usuario) buscarTarefasPendentes();
  }, [location.pathname]);


  const isActive = (path: string) => {
    return location.pathname === path ? 'bg-bravo-primary text-white' : '';
  };

  // Função para fechar o menu ao clicar em um link (apenas em mobile)
  const handleLinkClick = () => {
    if (window.innerWidth < 768) { // 768px é o breakpoint md do Tailwind
      fecharMenu();
    }
  };

  return (
    <div className={`bg-bravo-dark text-white w-64 flex-shrink-0 shadow-lg fixed md:static h-full z-50 transform transition-transform duration-300 ease-in-out ${menuAberto ? 'translate-x-0' : '-translate-x-full md:translate-x-0'} flex flex-col`}>
      <div className="h-[72px] px-6 flex items-center justify-center border-b border-gray-800 flex-shrink-0">
        <h1 className="text-xl font-bold">Bravo<span className="text-bravo-primary">Hub</span></h1>
      </div>
      
      {usuario && (
        <div className="p-4 border-b border-gray-800 flex-shrink-0">
          <div className="flex items-center">
            <img
              src={usuario.avatar || '/profilepicture.png'}
              alt="Avatar"
              className="w-10 h-10 rounded-full mr-3 border-2 border-bravo-primary object-cover"
              onError={(e) => { (e.target as HTMLImageElement).src = '/profilepicture.png'; }}
            />
            <div>
              <p className="font-medium">{usuario.nome}</p>
              <p className="text-sm text-gray-400">{usuario.cargo}</p>
            </div>
          </div>
        </div>
      )}
      
      <nav className="mt-4 flex-1 overflow-y-auto">
        <ul className="space-y-2">
          <li>
            <Link 
              to="/" 
              className={`flex items-center px-4 py-3 rounded-md hover:bg-gray-800 transition-colors ${isActive('/')}`}
              onClick={handleLinkClick}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              Dashboard
            </Link>
          </li>
          <li className="mb-1">
            <div className="px-4 py-2 text-sm text-gray-400 uppercase tracking-wider font-semibold">
              Tarefas
            </div>
          </li>
          <li>
            <Link 
              to="/kanban/recebidas" 
              className={`flex items-center px-4 py-3 rounded-md hover:bg-gray-800 transition-colors ${isActive('/kanban/recebidas')}`}
              onClick={handleLinkClick}
            >
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 4H6a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-2m-4-1v8m0 0l3-3m-3 3L9 8m-6 4h6" />
                  </svg>
                  <span>Tarefas Recebidas</span>
                </div>
                {tarefasPendentes > 0 && (
                  <span className="bg-orange-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center ml-2">
                    {tarefasPendentes > 99 ? '99+' : tarefasPendentes}
                  </span>
                )}
              </div>
            </Link>
          </li>
          <li>
            <Link 
              to="/kanban/enviadas" 
              className={`flex items-center px-4 py-3 rounded-md hover:bg-gray-800 transition-colors ${isActive('/kanban/enviadas')}`}
              onClick={handleLinkClick}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 4H6a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-2m-4-1v8m0 0l-3-3m3 3l3-3m-6 4h6" />
              </svg>
              Tarefas Enviadas
            </Link>
          </li>
          <li>
            <Link
              to="/busca-tarefas"
              className={`flex items-center px-4 py-3 rounded-md hover:bg-gray-800 transition-colors ${isActive('/busca-tarefas')}`}
              onClick={handleLinkClick}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              Busca de Tarefas
            </Link>
          </li>

          {/* Seção Comunicação Interna */}
          {usuario?.tipo !== 'expert' && (
            <>
              <li className="mb-1 mt-4">
                <div className="px-4 py-2 text-sm text-gray-400 uppercase tracking-wider font-semibold">
                  Comunicação Interna
                </div>
              </li>
              <li>
                <Link
                  to="/chat"
                  className={`flex items-center px-4 py-3 rounded-md hover:bg-gray-800 transition-colors ${isActive('/chat')}`}
                  onClick={handleLinkClick}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
                  </svg>
                  Chat Interno
                </Link>
              </li>
              <li>
                <Link
                  to="/bravo-ai"
                  className={`flex items-center px-4 py-3 rounded-md hover:bg-gray-800 transition-colors ${isActive('/bravo-ai')}`}
                  onClick={handleLinkClick}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 2v3m0 0H9m3 0h3" />
                    <rect x="4" y="5" width="16" height="13" rx="2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 11h.01M15 11h.01" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 15h6" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2 11h2M20 11h2" />
                  </svg>
                  Bravo AI
                </Link>
              </li>
              <li>
                <Link
                  to="/comunicacao"
                  className={`flex items-center px-4 py-3 rounded-md hover:bg-gray-800 transition-colors ${isActive('/comunicacao')}`}
                  onClick={handleLinkClick}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  Comunicação
                </Link>
              </li>
            </>
          )}

          {/* Itens visíveis apenas para usuários NÃO Expert */}
          {usuario?.tipo !== 'expert' && (
            <>
              <li>
                <Link
                  to="/avisos"
                  className={`flex items-center px-4 py-3 rounded-md hover:bg-gray-800 transition-colors ${isActive('/avisos')}`}
                  onClick={handleLinkClick}
                >
                  <div className="flex items-center justify-between w-full">
                    <div className="flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                      </svg>
                      <span>Avisos</span>
                    </div>
                    {avisosNaoLidos > 0 && (
                      <span className="bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center ml-2">
                        {avisosNaoLidos > 99 ? '99+' : avisosNaoLidos}
                      </span>
                    )}
                  </div>
                </Link>
              </li>
              <li className="mb-1 mt-4">
                <div className="px-4 py-2 text-sm text-gray-400 uppercase tracking-wider font-semibold">
                  Reservas
                </div>
              </li>
              <li>
                <Link 
                  to="/reservas" 
                  className={`flex items-center px-4 py-3 rounded-md hover:bg-gray-800 transition-colors ${isActive('/reservas')}`}
                  onClick={handleLinkClick}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Reservar Salas
                </Link>
              </li>
              <li>
                <Link 
                  to="/reservas/minhas" 
                  className={`flex items-center px-4 py-3 rounded-md hover:bg-gray-800 transition-colors ${isActive('/reservas/minhas')}`}
                  onClick={handleLinkClick}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                  Minhas Reservas
                </Link>
              </li>
              <li className="mb-1 mt-4">
                <div className="px-4 py-2 text-sm text-gray-400 uppercase tracking-wider font-semibold">
                  Relatórios
                </div>
              </li>
              <li>
                <Link
                  to="/relatorios"
                  className={`flex items-center px-4 py-3 rounded-md hover:bg-gray-800 transition-colors ${isActive('/relatorios')}`}
                  onClick={handleLinkClick}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Relatórios
                </Link>
              </li>
              {(usuario.tipo === 'admin' || ehLiderDepartamento) && (
                <li>
                  <Link
                    to="/relatorios/temporizador"
                    className={`flex items-center px-4 py-3 rounded-md hover:bg-gray-800 transition-colors ${isActive('/relatorios/temporizador')}`}
                    onClick={handleLinkClick}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Rel. Temporizador
                  </Link>
                </li>
              )}
            </>
          )}
          
          {/* Seção de liderança - visível apenas para líderes de departamento */}
          {usuario && ehLiderDepartamento && (
            <>
              <li className="mb-1 mt-4">
                <div className="px-4 py-2 text-sm text-gray-400 uppercase tracking-wider font-semibold">
                  Liderança
                </div>
              </li>
              <li>
                <Link 
                  to="/lider/atividades" 
                  className={`flex items-center px-4 py-3 rounded-md hover:bg-gray-800 transition-colors ${isActive('/lider/atividades')}`}
                  onClick={handleLinkClick}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Atividades do Departamento
                </Link>
              </li>
              <li>
                <Link 
                  to="/lider/tarefas-ativas-departamento" 
                  className={`flex items-center px-4 py-3 rounded-md hover:bg-gray-800 transition-colors ${isActive('/lider/tarefas-ativas-departamento')}`}
                  onClick={handleLinkClick}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  Tarefas Ativas Departamento
                </Link>
              </li>
            </>
          )}
          
          {/* Seção de administração - visível apenas para administradores */}
          {usuario && usuario.tipo === 'admin' && (
            <>
              <li className="mb-1 mt-4">
                <div className="px-4 py-2 text-sm text-gray-400 uppercase tracking-wider font-semibold">
                  Administração
                </div>
              </li>
              <li>
                <Link
                  to="/admin/monitor-chat"
                  className={`flex items-center px-4 py-3 rounded-md hover:bg-gray-800 transition-colors ${isActive('/admin/monitor-chat')}`}
                  onClick={handleLinkClick}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  Monitor de Chat
                </Link>
              </li>
              <li>
                <Link
                  to="/admin/usuarios"
                  className={`flex items-center px-4 py-3 rounded-md hover:bg-gray-800 transition-colors ${isActive('/admin/usuarios')}`}
                  onClick={handleLinkClick}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                  Gerenciar Usuários
                </Link>
              </li>
              <li>
                <Link 
                  to="/admin/kanban-global" 
                  className={`flex items-center px-4 py-3 rounded-md hover:bg-gray-800 transition-colors ${isActive('/admin/kanban-global')}`}
                  onClick={handleLinkClick}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                  Kanban Global
                </Link>
              </li>
              <li>
                <Link
                  to="/admin/busca-tarefas"
                  className={`flex items-center px-4 py-3 rounded-md hover:bg-gray-800 transition-colors ${isActive('/admin/busca-tarefas')}`}
                  onClick={handleLinkClick}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  Busca de Tarefas
                </Link>
              </li>
              <li>
                <Link
                  to="/admin/documentos-ia"
                  className={`flex items-center px-4 py-3 rounded-md hover:bg-gray-800 transition-colors ${isActive('/admin/documentos-ia')}`}
                  onClick={handleLinkClick}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Documentos da IA
                </Link>
              </li>
              <li>
                <Link
                  to="/admin/formularios"
                  className={`flex items-center px-4 py-3 rounded-md hover:bg-gray-800 transition-colors ${isActive('/admin/formularios')}`}
                  onClick={handleLinkClick}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  Formulários
                </Link>
              </li>
            </>
          )}

        </ul>
        
      </nav>

      {/* Rodapé fixo — Perfil e Configurações */}
      {usuario?.tipo !== 'expert' && (
        <div className="flex-shrink-0 border-t border-gray-800">
          <Link
            to="/perfil"
            className={`flex items-center px-4 py-3 hover:bg-gray-800 transition-colors ${isActive('/perfil')}`}
            onClick={handleLinkClick}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            <span className="text-sm">Perfil</span>
          </Link>
          <Link
            to="/configuracoes"
            className={`flex items-center px-4 py-3 hover:bg-gray-800 transition-colors ${isActive('/configuracoes')}`}
            onClick={handleLinkClick}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span className="text-sm">Configurações</span>
          </Link>
          <div className="px-4 py-2 border-t border-gray-800">
            <p className="text-xs uppercase text-gray-500 font-semibold tracking-wider">Versão</p>
            <p className="text-xs text-gray-400 mt-1">BravoHub</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sidebar;
